import React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { EvServiceInstallationData } from "../..";
import { getSelectedOptions } from "../../utility";
import EditSectionButton from "./components/EditSectionButton";

export type WizardFormPage =
  | "serviceOfferingSetup"
  | "chargerConfig"
  | "installationConfig"
  | "paymentAndAssumptions";

interface ReviewSummaryProps {
  data: EvServiceInstallationData;
  /** Sends the vendor back to the form page that owns the edited section. */
  onEdit: (page: WizardFormPage) => void;
}

const ReviewSummary: React.FC<ReviewSummaryProps> = ({ data, onEdit }) => {
  const { t } = useTranslation("services");
  const { serviceOfferingSetup, chargerConfig, installationConfig } = data;
  const payments = data.paymentAndAssumptionsConfigData;

  const section = (
    label: string,
    page: WizardFormPage,
    value: Parameters<typeof getSelectedOptions>[0],
  ) => (
    <React.Fragment key={label}>
      <EditSectionButton label={label} onClick={() => onEdit(page)} />
      {getSelectedOptions(value)}
    </React.Fragment>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        overflowWrap: "anywhere",
      }}
    >
      {section(
        t("wizard.summary.evse_types"),
        "serviceOfferingSetup",
        serviceOfferingSetup.supportedEVSETypes,
      )}
      {section(
        t("wizard.summary.buildings"),
        "serviceOfferingSetup",
        serviceOfferingSetup.buildingServiced,
      )}
      {section(
        t("wizard.summary.areas"),
        "serviceOfferingSetup",
        serviceOfferingSetup.areaServiced != null
          ? t("wizard.summary.areas_km", {
              km: serviceOfferingSetup.areaServiced,
            })
          : t("wizard.summary.not_set"),
      )}

      {section(
        t("wizard.summary.evse_pricing"),
        "chargerConfig",
        chargerConfig.evseType,
      )}

      {section(
        t("wizard.summary.cable"),
        "installationConfig",
        installationConfig.cableLength,
      )}
      {section(
        t("wizard.summary.inclusions"),
        "installationConfig",
        installationConfig.inclusions,
      )}
      {section(
        t("wizard.summary.add_ons"),
        "installationConfig",
        installationConfig.addOnServices,
      )}

      {section(
        t("wizard.summary.payment_types"),
        "paymentAndAssumptions",
        payments.paymentTypes,
      )}
      {section(
        t("wizard.summary.valid_for"),
        "paymentAndAssumptions",
        payments.quoteValidFor,
      )}
      {section(
        t("wizard.summary.payment_terms"),
        "paymentAndAssumptions",
        payments.paymentTerms,
      )}
      {section(
        t("wizard.summary.assumptions"),
        "paymentAndAssumptions",
        payments.assumptions,
      )}
    </Box>
  );
};

export default ReviewSummary;
